import { BiCardView } from '@euler/model/bi/dashboard';
import { useCallback, useMemo, useState } from 'react';
import { biCardFits, biRowsFor } from '../../functions/biGridLayout';

const kNoNeeds: Readonly<Record<string, number>> = {};

/**
 * The grid rows the cards that fit their content (KPI groups) need, as
 * they last measured their content at the width drawn: a card whose tiles
 * no longer wrap gives its rows back. Measured again at another width, and
 * only for cards that still fit their content.
 */
export function useBiFitNeeds(
  width: number,
  cards: Map<string, BiCardView>,
): {
  needs: Readonly<Record<string, number>>;
  /** A fitting card's content needs `px`, however tall it is drawn. */
  report: (cardId: string, px: number) => void;
} {
  const [state, setState] = useState<{
    width: number;
    needs: Record<string, number>;
  }>({ width, needs: {} });
  const report = useCallback(
    (cardId: string, px: number) => {
      const rows = biRowsFor(px);
      setState(current => {
        const needs = current.width === width ? current.needs : {};
        if (needs[cardId] === rows)
          return current.width === width ? current : { width, needs };
        return { width, needs: { ...needs, [cardId]: rows } };
      });
    },
    [width],
  );
  const measured = state.width === width ? state.needs : kNoNeeds;
  const needs = useMemo(() => {
    const fitting = Object.entries(measured).filter(([cardId]) => {
      const card = cards.get(cardId);
      return !!card && biCardFits(card.viz);
    });
    return fitting.length ? Object.fromEntries(fitting) : kNoNeeds;
  }, [measured, cards]);
  return { needs, report };
}
